import React from 'react';
import { ArrowUpRightIcon, BookOpenIcon, BrainCircuitIcon, BriefcaseBusinessIcon, CheckCircle2Icon, CircleCheckIcon, CompassIcon, CreditCardIcon, DownloadIcon, FileTextIcon, FlagIcon, GemIcon, HeartHandshakeIcon, InstagramIcon, LandmarkIcon, LightbulbIcon, LockKeyholeIcon, MailIcon, MedalIcon, NetworkIcon, ShieldCheckIcon, SparklesIcon, StarIcon, TargetIcon, TimerIcon, TrendingUpIcon, UserRoundCheckIcon, YoutubeIcon, ZapIcon, BoxIcon } from 'lucide-react';
import { DeferredVideo } from '../components/DeferredVideo';
import { FAQAccordion } from '../components/FAQAccordion';
import { MetaPixel } from '../components/MetaPixel';
import { PurchaseButton } from '../components/PurchaseButton';
import { Reveal } from '../components/Reveal';
import { SectionHeading } from '../components/SectionHeading';
import { useScreenInit } from '../useScreenInit.js';
const CHECKOUT_URL = 'https://www.avanzapro.store/cart/48591077441761:1';
type Tome = {
  number: string;
  title: string;
  copy: string;
  icon: BoxIcon;
  tone: string;
};
const tomes: Tome[] = [{
  number: 'TOMO 1',
  title: 'El Poder de la Dirección',
  copy: 'Elegí un norte claro y dejá de vivir reaccionando a lo que pasa.',
  icon: CompassIcon,
  tone: 'bg-[#0d5a48]'
}, {
  number: 'TOMO 2',
  title: 'Ingeniería de Metas Pro',
  copy: 'Convertí deseos en metas concretas, medibles y ejecutables.',
  icon: TargetIcon,
  tone: 'bg-[#176f5a]'
}, {
  number: 'TOMO 3',
  title: 'Cadenas de Identidad',
  copy: 'Detectá los patrones y creencias que todavía frenan tu crecimiento.',
  icon: LockKeyholeIcon,
  tone: 'bg-[#125d62]'
}, {
  number: 'TOMO 4',
  title: 'Propósito Construido',
  copy: 'Diseñá una vida con sentido desde valores, decisiones y dirección.',
  icon: HeartHandshakeIcon,
  tone: 'bg-[#9a4d43]'
}, {
  number: 'TOMO 5',
  title: 'La Ciencia de la Decisión',
  copy: 'Decidí con claridad, rapidez y coherencia. Menos vueltas, más dirección.',
  icon: BrainCircuitIcon,
  tone: 'bg-[#5c527c]'
}, {
  number: 'TOMO 6',
  title: 'Posicionamiento de Autoridad',
  copy: 'Construí una presencia más fuerte, visible y coherente.',
  icon: MedalIcon,
  tone: 'bg-[#7b5b21]'
}, {
  number: 'TOMO 7',
  title: 'Economía Digital Pro',
  copy: 'Creá ingresos digitales con estrategia, mentalidad y un modelo propio.',
  icon: LandmarkIcon,
  tone: 'bg-[#1f6b72]'
}, {
  number: 'TOMO 8',
  title: 'Sistemas de Alto Rendimiento',
  copy: 'Creá estructuras que sostengan tu avance, incluso sin motivación.',
  icon: TrendingUpIcon,
  tone: 'bg-[#3f5f39]'
}, {
  number: 'TOMO 9',
  title: 'El Capital Social',
  copy: 'Construí vínculos y redes que expandan tu vida en lugar de drenarla.',
  icon: NetworkIcon,
  tone: 'bg-[#7a4459]'
}, {
  number: 'TOMO 10',
  title: 'Filosofía de la Libertad',
  copy: 'Integrá todo en una forma de vivir más soberana y auténtica.',
  icon: FlagIcon,
  tone: 'bg-[#242f58]'
}];
const testimonials = [{
  quote: 'Arranqué sin grandes expectativas y terminé reescribiendo mi Declaración de Dirección tres veces. Ahí me di cuenta de que seguía viviendo para otros.',
  name: 'Marcela R.',
  place: 'Montevideo'
}, {
  quote: 'Tenía ganas de emprender, pero no sabía por dónde empezar. El Framework de las 3 Brújulas me dio claridad en 45 minutos.',
  name: 'Diego F.',
  place: 'Punta del Este'
}, {
  quote: 'No es motivación vacía: es un sistema concreto. Al día 4 ya había tomado una decisión que venía postergando hace meses.',
  name: 'Carolina M.',
  place: 'Salto'
}, {
  quote: 'Me hizo ver que mi problema no era falta de ganas. Era falta de dirección. Eso me cambió la manera de organizar mi semana.',
  name: 'Andrés P.',
  place: 'Canelones'
}, {
  quote: 'El Test de Identidad me mostró por dónde empezar. Antes quería cambiar todo junto y terminaba abandonando.',
  name: 'Lucía M.',
  place: 'Maldonado'
}];
const faqs = [{
  question: '¿Cómo se realiza el pago?',
  answer: 'El pago se realiza de forma 100% segura mediante Mercado Pago. Podés pagar con tarjeta de crédito, tarjeta de débito o saldo disponible en tu cuenta.'
}, {
  question: '¿Cómo recibo el material después de la compra?',
  answer: 'Una vez confirmado el pago, recibís el acceso por correo electrónico para descargar el material.'
}, {
  question: '¿En qué formato viene el material?',
  answer: 'El material viene en formato PDF. Podés descargarlo y leerlo desde celular, tablet o computadora.'
}, {
  question: '¿Necesito instalar alguna aplicación especial?',
  answer: 'No. Solo necesitás poder abrir archivos PDF, algo que cualquier celular, tablet o computadora permite hacer.'
}, {
  question: '¿Tengo que leer los 10 tomos en orden?',
  answer: 'No necesariamente. Podés empezar por el tomo que más conecte con tu situación actual. Si no sabés por dónde empezar, usás el Test de Identidad incluido.'
}, {
  question: '¿Esto reemplaza terapia o coaching?',
  answer: 'No. Es un sistema de herramientas prácticas de pensamiento, decisión y acción personal. No reemplaza terapia, tratamiento psicológico, médico ni acompañamiento profesional.'
}, {
  question: '¿Es pago único o suscripción?',
  answer: 'Es pago único. No hay mensualidades, suscripciones ni cobros recurrentes.'
}, {
  question: '¿Qué pasa si compro y no me sirve?',
  answer: 'Tenés garantía de 7 días. Si sentís que no era para vos, escribís y se te devuelve el 100% de tu inversión.'
}];
const needIndex = [['No sé hacia dónde voy', 'Tomo 1: El Poder de la Dirección'], ['Tengo metas, pero no las sostengo', 'Tomo 2: Ingeniería de Metas Pro'], ['Siento que algo interno me frena', 'Tomo 3: Cadenas de Identidad'], ['No encuentro sentido en lo que hago', 'Tomo 4: Propósito Construido'], ['Me cuesta decidir y avanzo lento', 'Tomo 5: La Ciencia de la Decisión'], ['Quiero hacerme visible y valer más', 'Tomo 6: Posicionamiento de Autoridad'], ['Quiero crear ingresos digitales con dirección', 'Tomo 7: Economía Digital para Pro'], ['Estoy cansado y necesito estructura', 'Tomo 8: Sistemas de Alto Rendimiento'], ['Mi entorno me drena o no me impulsa', 'Tomo 9: El Capital Social'], ['Quiero vivir con más libertad y soberanía', 'Tomo 10: Filosofía de la Libertad']];
function PaymentNote({
  inverted = false
}: {
  inverted?: boolean;
}) {
  return <div className={`mt-4 space-y-1 text-xs font-medium leading-5 ${inverted ? 'text-emerald-50/75' : 'text-slate-500'}`} data-id="element-22">
      <p data-id="element-23">🔒 Pago seguro mediante Mercado Pago</p>
      <p data-id="element-24">📄 Material 100% digital en PDF</p>
      <p data-id="element-25">🛡️ Garantía de 7 días</p>
    </div>;
}
function LibraryPreview() {
  return <div className="relative mx-auto w-full max-w-md" data-id="element-26">
      <div className="absolute -right-4 top-9 hidden h-40 w-40 rounded-full border border-amber-300/25 lg:block" data-id="element-27" />
      <div className="relative overflow-hidden rounded-[2rem] border border-emerald-700 bg-[#f7f3ea] p-2 shadow-2xl shadow-black/30 sm:p-3" data-id="element-28">
        <div className="relative overflow-hidden rounded-[1.45rem] bg-[#093e33]" data-id="element-29">
          <div className="relative aspect-[4/5] w-full bg-[#f4f4f2]" data-id="element-30">
            <video autoPlay loop muted playsInline preload="auto" aria-hidden="true" className="absolute inset-0 h-full w-full object-contain mix-blend-multiply" src="https://cdn.magicpatterns.com/uploads/oNrRbBUXd9qcvRSnq8xpLq/hf_20260712_231519_20dc7dac-cb1f-4e3a-84a4-253964882d6a.mp4" data-id="element-31" />
          </div>
          <div className="relative z-10 p-5 text-white sm:p-7" data-id="element-32">
            <div className="flex items-center justify-between text-[9px] font-black tracking-[0.18em] text-amber-300" data-id="element-33"></div>
            <div className="mt-6 pb-2" data-id="element-34">
              <h2 className="mt-2 text-4xl font-black leading-[0.9] tracking-[-0.06em] sm:text-5xl" data-id="element-35">
                Pensamientos
                <br data-id="element-36" />
                <span className="text-amber-300" data-id="element-37">Fuera de Serie</span>
              </h2>
              <p className="mt-4 max-w-[15rem] text-xs leading-5 text-emerald-50" data-id="element-38">
                Sentí, Decidí y Actuá como la Persona que ya Elegís Ser
              </p>
            </div>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 rounded-2xl bg-white p-3 text-center text-emerald-950 sm:mt-3" data-id="element-39">
          {[['10', 'Tomos'], ['450', 'Páginas'], ['7 días', 'Garantía']].map(([value, label]) => <div key={label} data-id="element-40">
              <strong className="block text-base font-black leading-none" data-id="element-41">
                {value}
              </strong>
              <span className="mt-1 block text-[9px] font-bold text-slate-500" data-id="element-42">
                {label}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
export function LandingPage() {
  useScreenInit();
  return <main className="w-full overflow-hidden bg-[#f7f5ef] text-[#0b2e27]" data-id="element-43">
      <MetaPixel data-id="element-44" />
      <div className="sticky top-0 z-40 bg-amber-400 px-4 py-2 text-center text-[10px] font-black tracking-wide text-emerald-950 sm:text-[11px]" data-id="element-45">
        🔥 Hoy comienza tu cambio. No lo postergues más!
      </div>

      <section className="bg-[#062f27] px-5 pb-16 pt-7 text-white sm:px-8 lg:pb-24" data-id="element-46">
        <nav aria-label="Navegación principal" className="mx-auto flex max-w-6xl items-center justify-between" data-id="element-47">
          <a href="#" className="leading-tight" data-id="element-48">
            <span className="block text-sm font-black tracking-[0.16em] text-amber-300" data-id="element-49">
              ✦ AVANZA PRO ✦
            </span>
            <span className="block text-[10px] font-semibold text-emerald-100/70" data-id="element-50">
              La Facultad del Nuevo Mundo
            </span>
          </a>
          <a href="#biblioteca" className="text-xs font-bold text-emerald-100 transition hover:text-amber-300" data-id="element-51">
            Ver la biblioteca
          </a>
        </nav>

        <div className="mx-auto grid max-w-6xl items-center gap-10 pt-8 sm:pt-10 lg:grid-cols-[1.05fr_.95fr] lg:gap-12 lg:pt-14" data-id="element-52">
          <Reveal data-id="element-53">
            <h1 className="mx-auto mt-2 max-w-xl text-center text-4xl font-black leading-[0.98] tracking-[-0.06em] sm:text-5xl lg:mt-5 lg:text-6xl" data-id="element-54">
              <span className="block" data-id="element-55">Cuando tu mente te diga</span>
              <span className="mt-1 block text-amber-300" data-id="element-56">
                “no sé por dónde empezar”
              </span>
              <span className="mt-1 block" data-id="element-57">vos ya vas a tener el mapa.</span>
            </h1>
            <div className="hidden lg:block" data-id="element-58">
              <p className="mt-6 max-w-xl text-base leading-7 text-emerald-50/80" data-id="element-59">
                Un sistema práctico de transformación personal para personas que
                saben que pueden más, pero necesitan claridad, dirección y un
                método concreto para empezar a vivir desde su próxima versión.
              </p>
              <p className="mt-3 max-w-xl text-sm leading-6 text-emerald-100/65" data-id="element-60">
                Sentí con claridad, decidí con propósito y actuá como la persona
                que ya elegís ser.
              </p>
            </div>
            <div className="mt-8 hidden lg:block" data-id="element-61">
              <PurchaseButton data-id="element-62">
                QUIERO MI BIBLIOTECA FUERA DE SERIE
              </PurchaseButton>
              <PaymentNote inverted data-id="element-63" />
            </div>
          </Reveal>

          <Reveal delay={0.1} data-id="element-64">
            <LibraryPreview data-id="element-65" />
          </Reveal>

          <div className="text-center lg:hidden" data-id="element-66">
            <div className="mx-auto max-w-xl text-center" data-id="element-67">
              <p className="text-base leading-7 text-emerald-50/80" data-id="element-68">
                Un sistema práctico de transformación personal para personas que
                saben que pueden más, pero necesitan claridad, dirección y un
                método concreto para empezar a vivir desde su próxima versión.
              </p>
              <p className="mt-3 text-sm leading-6 text-emerald-100/65" data-id="element-69">
                Sentí con claridad, decidí con propósito y actuá como la persona
                que ya elegís ser.
              </p>
            </div>
            <div className="mt-7" data-id="element-70">
              <PurchaseButton data-id="element-71">
                QUIERO MI BIBLIOTECA FUERA DE SERIE
              </PurchaseButton>
            </div>
            <PaymentNote inverted data-id="element-72" />
          </div>
        </div>
      </section>

      <section className="section-shell bg-[#edf5f0]" data-id="element-73">
        <Reveal data-id="element-74">
          <SectionHeading eyebrow="¿ALGUNA VEZ TE PASÓ?" title="Sabés que podés más, pero seguís haciendo lo mismo." description="Por fuera todo parece normal. Trabajás, cumplís y seguís adelante. Pero por dentro vuelve una sensación: “Yo no nací solamente para esto.”" data-id="element-75" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-3" data-id="element-76">
          {[[TimerIcon, 'Te esforzás, pero no avanzás como querés', 'El cansancio, la intención y las ganas están. Pero los resultados no siempre te acercan a la vida que querés construir.'], [LightbulbIcon, 'Tenés ideas, pero no sabés por dónde empezar', 'Cuando llega el momento de actuar aparece la confusión, el miedo a equivocarte y la decisión queda para después.'], [TrendingUpIcon, 'Cada año se parece demasiado al anterior', 'Cambian algunas cosas por fuera, pero tu próxima versión sigue esperando un sistema que la sostenga.']].map(([Icon, title, copy], index) => {
          const CardIcon = Icon as BoxIcon;
          return <Reveal key={String(title)} delay={index * 0.05} data-id="element-77">
                <article className="h-full rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm" data-id="element-78">
                  <CardIcon className="h-9 w-9 rounded-lg bg-[#fce8df] p-2 text-[#b65347]" data-id="element-79" />
                  <h3 className="mt-5 text-lg font-black" data-id="element-80">{String(title)}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-81">
                    {String(copy)}
                  </p>
                </article>
              </Reveal>;
        })}
        </div>
      </section>

      <section className="section-shell bg-white" data-id="element-82">
        <div className="mx-auto max-w-4xl" data-id="element-83">
          <Reveal data-id="element-84">
            <SectionHeading eyebrow="Y LO PEOR ES ESTO" title="Cada vez que postergás tu cambio, algo adentro se apaga un poco." data-id="element-85" />
          </Reveal>
          <Reveal delay={0.06} data-id="element-86">
            <ul className="mt-10 space-y-4 rounded-2xl bg-[#fbefea] p-6 sm:p-8" data-id="element-87">
              {['Buscás motivación, pero a los pocos días volvés al mismo lugar.', 'Consumís contenido, frases y consejos, pero nada termina de ordenarte.', 'Sabés que necesitás cambiar, pero no sabés exactamente qué hacer primero.', 'Empezás con entusiasmo y después te dispersás.', 'Sentís que vivís más respondiendo a otros que decidiendo por vos.', 'Querés una vida diferente, pero seguís funcionando con pensamientos viejos.'].map(item => <li key={item} className="flex gap-3 text-sm font-semibold leading-6 text-[#5a2d28]" data-id="element-88">
                  <span className="font-black text-[#ba5b50]" data-id="element-89">×</span>
                  {item}
                </li>)}
            </ul>
            <p className="mx-auto mt-9 max-w-3xl text-center text-xl font-black leading-tight sm:text-2xl" data-id="element-90">
              No necesitás más ruido. Necesitás un sistema.
            </p>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-6 text-slate-600" data-id="element-91">
              Un mapa para ver dónde estás, qué te está frenando y qué decisión
              podés tomar ahora.
            </p>
            <div className="mt-7 text-center" data-id="element-92">
              <PurchaseButton data-id="element-93">QUIERO RESOLVER ESTO HOY</PurchaseButton>
            </div>
            <p className="mt-4 text-center text-xs text-slate-500" data-id="element-94">
              Acceso inmediato · Pago único · Sin suscripciones · Garantía de 7
              días
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-shell bg-[#eaf2ec]" data-id="element-95">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-96">
          <Reveal data-id="element-97">
            <p className="eyebrow" data-id="element-98">LA SOLUCIÓN</p>
            <h2 className="mt-3 text-4xl font-black leading-[1.02] tracking-[-0.05em]" data-id="element-99">
              Pensamientos Fuera de Serie
            </h2>
            <p className="mt-2 text-lg font-bold text-emerald-700" data-id="element-100">
              Sentí, Decidí y Actuá como la Persona que ya Elegís Ser.
            </p>
            <p className="mt-5 text-base leading-7 text-slate-600" data-id="element-101">
              Una biblioteca digital premium de 10 tomos creada para transformar
              tu manera de pensar, decidir y actuar. No es motivación vacía ni
              teoría para acumular: es un sistema práctico para mirar tu vida
              con honestidad y empezar a actuar desde una identidad más clara.
            </p>
            <p className="mt-4 text-sm leading-6 text-slate-600" data-id="element-102">
              Comprás, recibís el acceso y empezás. Sin envíos físicos, sin
              instalaciones complicadas, sin suscripciones y sin vencimientos.
            </p>
          </Reveal>

          <Reveal delay={0.08} data-id="element-103">
            <div className="relative mx-auto aspect-[9/19.5] w-[260px] overflow-hidden rounded-[2.5rem] border-[8px] border-[#062f27] bg-[#062f27] shadow-2xl sm:w-[280px]" data-id="element-104">
              <div className="absolute inset-x-0 top-0 z-20 flex h-6 justify-center" data-id="element-105">
                <div className="h-5 w-24 rounded-b-2xl bg-[#062f27]" data-id="element-106"></div>
              </div>
              <DeferredVideo src="https://cdn.magicpatterns.com/uploads/sBXBRjwxkw4EjvjyTnWxDc/0713.mov#t=0.001" className="h-full w-full object-cover" interactive data-id="element-107" />
            </div>
          </Reveal>
        </div>
        <div className="mt-8 text-center" data-id="element-108">
          <PurchaseButton data-id="element-109">EMPEZAR MI CAMBIO HOY</PurchaseButton>
        </div>
      </section>

      <section id="biblioteca" className="section-shell bg-white" data-id="element-110">
        <Reveal data-id="element-111">
          <SectionHeading eyebrow="LO QUE RECIBÍS" title="10 tomos en PDF. 450 páginas. Una biblioteca completa para transformar tu forma de vivir." description="Cada tomo trabaja un área clave. Juntos forman un mapa para ordenar tu mente, tomar mejores decisiones y construir una vida con dirección real." data-id="element-112" />
        </Reveal>

        <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-113">
          {tomes.map((tome, index) => {
          const Icon = tome.icon;
          return <Reveal key={tome.number} delay={index * 0.03} data-id="element-114">
                <article className="h-full overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm" data-id="element-115">
                  <div className={`${tome.tone} p-5 text-white`} data-id="element-116">
                    <p className="text-[10px] font-black tracking-[0.16em]" data-id="element-117">
                      {tome.number}
                    </p>
                    <Icon className="mt-4 h-8 w-8 text-amber-300" data-id="element-118" />
                    <h3 className="mt-5 text-xl font-black leading-tight" data-id="element-119">
                      {tome.title}
                    </h3>
                  </div>
                  <div className="p-5" data-id="element-120">
                    <p className="text-sm leading-6 text-slate-600" data-id="element-121">
                      {tome.copy}
                    </p>
                    <p className="mt-5 flex items-center gap-1 border-t border-emerald-100 pt-4 text-xs font-bold text-emerald-700" data-id="element-122">
                      <FileTextIcon size={13} aria-hidden="true" data-id="element-123" />
                      PDF · Incluido
                    </p>
                  </div>
                </article>
              </Reveal>;
        })}
        </div>

        <div className="mt-8 text-center" data-id="element-124">
          <PurchaseButton data-id="element-125">QUIERO LOS 10 TOMOS COMPLETOS</PurchaseButton>
          <p className="mt-3 text-xs text-slate-500" data-id="element-126">
            Incluye 450 páginas + Kit de Ejecución gratis
          </p>
        </div>
      </section>

      <section className="section-shell bg-[#fff8e8]" data-id="element-127">
        <Reveal data-id="element-128">
          <article className="mx-auto max-w-4xl rounded-3xl border border-amber-300 bg-white p-6 shadow-sm sm:p-8" data-id="element-129">
            <div className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-start" data-id="element-130">
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-amber-400 text-emerald-950" data-id="element-131">
                <GemIcon size={30} data-id="element-132" />
              </div>
              <div data-id="element-133">
                <p className="text-xs font-black tracking-[0.14em] text-amber-700" data-id="element-134">
                  BONUS ESPECIAL · INCLUIDO SIN COSTO EXTRA
                </p>
                <h2 className="mt-2 text-2xl font-black" data-id="element-135">
                  Kit de Ejecución Fuera de Serie
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600" data-id="element-136">
                  Porque leer no alcanza. Con tu compra recibís herramientas
                  para llevar la biblioteca a la acción desde el primer día.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-137">
                  {[['Planner de Ejecución Avanza Pro', 'Planificá tu semana, ordená prioridades y convertí aprendizajes en acciones concretas.', BriefcaseBusinessIcon], ['Test de Identidad', 'Detectá qué tomo conviene trabajar primero según tu momento actual.', UserRoundCheckIcon]].map(([title, copy, Icon]) => {
                  const KitIcon = Icon as BoxIcon;
                  return <div key={String(title)} className="rounded-xl bg-amber-50 p-4" data-id="element-138">
                        <KitIcon className="h-5 w-5 text-amber-700" data-id="element-139" />
                        <h3 className="mt-2 text-sm font-black" data-id="element-140">
                          {String(title)}
                        </h3>
                        <p className="mt-1 text-xs leading-5 text-slate-600" data-id="element-141">
                          {String(copy)}
                        </p>
                      </div>;
                })}
                </div>
                <p className="mt-5 text-sm font-bold text-emerald-800" data-id="element-142">
                  Valor real del Kit:{' '}
                  <span className="line-through" data-id="element-143">$1.900</span> pesos uruguayos ·
                  Incluido gratis con tu compra
                </p>
              </div>
            </div>
          </article>
          <div className="mt-7 text-center" data-id="element-144">
            <PurchaseButton data-id="element-145">QUIERO MIS 10 TOMOS + BONOS AHORA</PurchaseButton>
          </div>
        </Reveal>
      </section>

      <section className="section-shell bg-[#062f27] text-white" data-id="element-146">
        <Reveal data-id="element-147">
          <SectionHeading inverted eyebrow="¿CÓMO RECIBO MI MATERIAL?" title="Tres pasos simples. Sin complicaciones." data-id="element-148" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-3" data-id="element-149">
          {[[CreditCardIcon, 'PASO 1', 'Hacés tu pago', 'Completás la compra de forma segura mediante Mercado Pago.'], [MailIcon, 'PASO 2', 'Recibís tu email', 'Después de confirmado el pago, recibís el acceso al material en tu correo.'], [DownloadIcon, 'PASO 3', 'Descargás tus PDFs', 'Guardás la biblioteca en tu celular, tablet o computadora para siempre.']].map(([Icon, step, title, copy]) => {
          const StepIcon = Icon as BoxIcon;
          return <article key={String(step)} className="rounded-2xl bg-emerald-900 p-6" data-id="element-150">
                <p className="text-[10px] font-black tracking-[0.15em] text-amber-300" data-id="element-151">
                  {String(step)}
                </p>
                <StepIcon className="mt-4 h-9 w-9 text-emerald-300" data-id="element-152" />
                <h3 className="mt-5 text-xl font-black" data-id="element-153">{String(title)}</h3>
                <p className="mt-3 text-sm leading-6 text-emerald-100/75" data-id="element-154">
                  {String(copy)}
                </p>
              </article>;
        })}
        </div>
        <div className="mt-9 text-center" data-id="element-155">
          <p className="text-sm font-bold text-emerald-100" data-id="element-156">
            Sin esperas. Sin envíos físicos. Sin suscripciones.
          </p>
          <PurchaseButton className="mt-6" data-id="element-157">ACCEDER AHORA</PurchaseButton>
        </div>
      </section>

      <section className="section-shell bg-[#edf5f0]" data-id="element-158">
        <Reveal data-id="element-159">
          <SectionHeading eyebrow="PAGO SEGURO" title="Pagás como te resulte más cómodo." data-id="element-160" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-3" data-id="element-161">
          {[[CreditCardIcon, 'Tarjeta de crédito', 'Todas las tarjetas aceptadas mediante Mercado Pago.'], [CheckCircle2Icon, 'Tarjeta de débito', 'Débito inmediato, simple y seguro.'], [LockKeyholeIcon, 'Saldo de Mercado Pago', 'Usá tu saldo disponible si ya tenés cuenta.']].map(([Icon, title, copy]) => {
          const PaymentIcon = Icon as BoxIcon;
          return <article key={String(title)} className="rounded-2xl bg-white p-6 text-center shadow-sm" data-id="element-162">
                <PaymentIcon className="mx-auto h-8 w-8 text-emerald-600" data-id="element-163" />
                <h3 className="mt-3 font-black" data-id="element-164">{String(title)}</h3>
                <p className="mt-2 text-sm text-slate-600" data-id="element-165">{String(copy)}</p>
              </article>;
        })}
        </div>
      </section>

      <section className="section-shell bg-white" data-id="element-166">
        <Reveal data-id="element-167">
          <SectionHeading eyebrow="EL DIFERENCIAL" title="Un sistema pensado para lo que de verdad pasa en la vida." description="No para una versión perfecta de vos. Para tus dudas, tus responsabilidades y tus ganas reales de cambiar." data-id="element-168" />
        </Reveal>
        <div className="mx-auto mt-10 max-w-5xl overflow-hidden rounded-2xl border border-emerald-100" data-id="element-169">
          <div className="grid grid-cols-2 bg-[#062f27] text-xs font-black tracking-wide text-white" data-id="element-170">
            <div className="p-4 text-white/65" data-id="element-171">LO QUE EXISTE HOY</div>
            <div className="bg-emerald-700 p-4" data-id="element-172">
              PENSAMIENTOS FUERA DE SERIE
            </div>
          </div>
          {[['Requiere motivación constante.', 'Te da un sistema completo de transformación.'], ['Te da frases lindas, pero poca estructura.', 'Trabaja 10 áreas clave de tu vida.'], ['Te inspira por un rato y después se enfría.', 'Incluye ejercicios, marcos y herramientas aplicables.'], ['Te deja solo frente a tus decisiones.', 'Te ayuda a detectar qué te frena y qué hacer después.'], ['Te hace consumir más información sin aplicar.', 'Incluye Kit de Ejecución para pasar de lectura a acción.'], ['Tiene acceso limitado o depende de una plataforma.', 'Es 100% digital, descargable y tuyo para siempre.']].map(([oldWay, newWay]) => <div key={oldWay} className="grid grid-cols-2 border-t border-emerald-100 text-xs sm:text-sm" data-id="element-173">
              <div className="p-4 text-slate-500" data-id="element-174">{oldWay}</div>
              <div className="bg-emerald-50 p-4 font-bold text-emerald-900" data-id="element-175">
                {newWay}
              </div>
            </div>)}
        </div>
        <div className="mt-8 text-center" data-id="element-176">
          <PurchaseButton data-id="element-177">QUIERO UN SISTEMA, NO MÁS EXCUSAS</PurchaseButton>
        </div>
      </section>

      <section className="section-shell bg-[#eaf2ec]" data-id="element-178">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-179">
          <Reveal data-id="element-180">
            <article className="rounded-3xl border-4 border-[#0b3f34] bg-white p-6 shadow-xl shadow-emerald-950/10" data-id="element-181">
              <p className="text-[10px] font-black tracking-[0.15em] text-emerald-600" data-id="element-182">
                EJEMPLO DE TRABAJO INTERNO
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-[-0.04em]" data-id="element-183">
                Declaración de Dirección
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600" data-id="element-184">
                Una herramienta central para responder con honestidad qué parte
                de tu vida querés empezar a construir.
              </p>
              <div className="mt-5 space-y-2" data-id="element-185">
                {['¿Qué quiero construir realmente?', '¿Qué versión de mí ya no quiero seguir alimentando?', '¿Qué decisiones estoy postergando por miedo?', '¿Qué hábitos sostienen mi identidad actual?', '¿Qué tendría que hacer hoy la persona que elegí ser?'].map(question => <p key={question} className="rounded-lg bg-[#f2f7f3] px-3 py-2.5 text-sm font-semibold text-emerald-950" data-id="element-186">
                    {question}
                  </p>)}
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.08} data-id="element-187">
            <p className="eyebrow" data-id="element-188">ASÍ SE TRABAJA CADA TOMO</p>
            <h2 className="mt-3 text-3xl font-black leading-tight" data-id="element-189">
              Todo lo que necesitás para avanzar con claridad.
            </h2>
            <ul className="mt-6 space-y-3 text-sm font-semibold" data-id="element-190">
              {['Conceptos profundos explicados de forma simple.', 'Ejercicios prácticos para aplicar a tu vida real.', 'Preguntas incómodas para romper el piloto automático.', 'Marcos de decisión para ordenar tu mente.', 'Herramientas para convertir claridad en acción.', 'Planes de ejecución para no quedarte solo en la lectura.'].map(item => <li key={item} className="flex gap-2" data-id="element-191">
                  <CircleCheckIcon className="h-5 w-5 shrink-0 text-emerald-600" data-id="element-192" />
                  {item}
                </li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="section-shell bg-white" data-id="element-193">
        <Reveal data-id="element-194">
          <SectionHeading eyebrow="ÍNDICE POR NECESIDAD" title="Encontrá el tomo ideal según tu momento." data-id="element-195" />
        </Reveal>
        <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border border-emerald-100" data-id="element-196">
          <div className="grid grid-cols-[1.2fr_1fr] bg-[#062f27] p-4 text-xs font-black text-white" data-id="element-197">
            <span data-id="element-198">SI ESTÁS SINTIENDO…</span>
            <span data-id="element-199">IR AL TOMO</span>
          </div>
          {needIndex.map(([need, tome]) => <div key={need} className="grid grid-cols-[1.2fr_1fr] border-t border-emerald-100 text-xs sm:text-sm" data-id="element-200">
              <span className="p-4" data-id="element-201">{need}</span>
              <span className="bg-emerald-50 p-4 font-bold text-emerald-800" data-id="element-202">
                {tome}
              </span>
            </div>)}
        </div>
        <p className="mx-auto mt-7 max-w-2xl text-center text-sm leading-6 text-slate-600" data-id="element-203">
          No importa si hoy estás sin dirección, sin claridad, sin energía o con
          miedo a decidir. Hay un tomo pensado para tu momento exacto.
        </p>
        <div className="mt-7 text-center" data-id="element-204">
          <PurchaseButton data-id="element-205">ENCONTRAR MI PUNTO DE PARTIDA</PurchaseButton>
        </div>
      </section>

      <section id="comprar" className="section-shell bg-[#062f27] text-white" data-id="element-206">
        <Reveal data-id="element-207">
          <SectionHeading inverted eyebrow="UNA SOLA INVERSIÓN" title="Todo el sistema completo por solo $990 pesos uruguayos." data-id="element-208" />
        </Reveal>
        <Reveal delay={0.06} className="mx-auto mt-10 max-w-4xl rounded-3xl bg-white p-6 text-emerald-950 shadow-2xl sm:p-8" data-id="element-209">
          <div className="grid grid-cols-[1.45fr_.85fr] border-b border-emerald-100 pb-3 text-xs font-black text-emerald-700" data-id="element-210">
            <span data-id="element-211">QUÉ RECIBÍS</span>
            <span data-id="element-212">FORMATO</span>
          </div>
          {[...tomes.map(tome => tome.title), 'Bonus: Planner de Ejecución Avanza Pro', 'Bonus: Test de Identidad', 'Acceso inmediato y de por vida', 'Pago único', 'Garantía incondicional de 7 días'].map((item, index) => <div key={item} className="grid grid-cols-[1.45fr_.85fr] border-b border-emerald-50 py-3 text-xs sm:text-sm" data-id="element-213">
              <span className="font-semibold" data-id="element-214">
                {index < 10 ? `Tomo ${index + 1}: ${item}` : item}
              </span>
              <span className="font-medium text-emerald-700" data-id="element-215">
                {index < 12 ? 'PDF · Incluido' : index === 12 ? 'Al instante' : index === 13 ? 'Sin mensualidades' : 'Incluida'}
              </span>
            </div>)}
          <div className="pt-8 text-center" data-id="element-216">
            <p className="text-xs font-black tracking-[0.15em] text-emerald-600" data-id="element-217">
              TODO ESTO POR SOLO
            </p>
            <p className="mt-2 text-5xl font-black text-emerald-700" data-id="element-218">$990</p>
            <p className="text-lg font-bold" data-id="element-219">pesos uruguayos</p>
            <p className="mt-2 text-sm font-semibold text-[#b65347]" data-id="element-220">
              Antes: <span className="line-through" data-id="element-221">$1.990</span> · Oferta de
              lanzamiento por tiempo limitado
            </p>
            <p className="mt-3 text-sm text-slate-600" data-id="element-222">
              100% PDF · Acceso inmediato · Pago seguro mediante Mercado Pago.
            </p>
            <div className="mt-6" data-id="element-223">
              <PurchaseButton data-id="element-224">ASEGURAR MI BIBLIOTECA COMPLETA</PurchaseButton>
            </div>
            <PaymentNote data-id="element-225" />
          </div>
        </Reveal>
      </section>

      <section className="section-shell bg-[#edf5f0]" data-id="element-226">
        <Reveal data-id="element-227">
          <SectionHeading eyebrow="PERSONAS QUE YA LO APLICARON" title="Esto es lo que dicen quienes empezaron a pensar fuera de serie." data-id="element-228" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-229">
          {testimonials.map((testimonial, index) => <Reveal delay={index * 0.04} key={testimonial.name} data-id="element-230">
              <article className="h-full rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm" data-id="element-231">
                <div className="flex gap-1 text-amber-400" data-id="element-232">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-4 w-4 fill-current" data-id="element-233" />)}
                </div>
                <blockquote className="mt-4 text-sm leading-6 text-slate-700" data-id="element-234">
                  “{testimonial.quote}”
                </blockquote>
                <div className="mt-5 border-t border-emerald-100 pt-3" data-id="element-235">
                  <p className="text-sm font-black" data-id="element-236">{testimonial.name}</p>
                  <p className="text-xs text-slate-500" data-id="element-237">{testimonial.place}</p>
                </div>
              </article>
            </Reveal>)}
        </div>
        <div className="mt-9 text-center" data-id="element-238">
          <p className="text-sm font-semibold text-slate-600" data-id="element-239">
            Ellos no esperaron a tener más tiempo. Empezaron con una decisión.
          </p>
          <PurchaseButton className="mt-5" data-id="element-240">
            YO TAMBIÉN QUIERO EMPEZAR
          </PurchaseButton>
        </div>
      </section>

      <section className="section-shell bg-[#fff8e8]" data-id="element-241">
        <Reveal data-id="element-242">
          <div className="mx-auto max-w-5xl rounded-3xl border border-amber-300 bg-white p-7 text-center shadow-sm sm:p-10" data-id="element-243">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-amber-500" data-id="element-244" />
            <p className="mt-4 text-xs font-black tracking-[0.15em] text-amber-700" data-id="element-245">
              CERO RIESGO PARA VOS
            </p>
            <h2 className="mt-2 text-3xl font-black" data-id="element-246">
              Probalo durante 7 días. Sin preguntas.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-247">
              Si dentro de los primeros 7 días sentís que el material no era lo
              que esperabas, te devolvemos el 100% de tu dinero. Sin formularios
              largos, sin vueltas y sin explicaciones incómodas.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-3" data-id="element-248">
              {[['7 días completos', 'Una semana entera para entrar al material y empezar a aplicar.'], ['100% de tu dinero', 'Si no es para vos, te devolvemos tu inversión completa.'], ['Sin preguntas', 'No tenés que justificarte. Tu decisión se respeta.']].map(([title, copy]) => <div key={title} data-id="element-249">
                  <LightbulbIcon className="mx-auto h-6 w-6 text-amber-500" data-id="element-250" />
                  <h3 className="mt-2 font-black" data-id="element-251">{title}</h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-252">{copy}</p>
                </div>)}
            </div>
            <PurchaseButton className="mt-8" data-id="element-253">PROBAR SIN RIESGO</PurchaseButton>
          </div>
        </Reveal>
      </section>

      <section className="section-shell bg-white" data-id="element-254">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[.65fr_1.35fr]" data-id="element-255">
          <Reveal data-id="element-256">
            <div className="mx-auto grid h-44 w-44 place-items-center rounded-full border border-emerald-100 bg-[#eaf2ec] text-center text-emerald-800" data-id="element-257">
              <div data-id="element-258">
                <SparklesIcon className="mx-auto h-10 w-10 text-amber-500" data-id="element-259" />
                <p className="mt-2 text-xs font-black tracking-[0.14em]" data-id="element-260">
                  AVANZA PRO
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08} data-id="element-261">
            <p className="eyebrow" data-id="element-262">¿QUIÉN ESTÁ DETRÁS DE ESTO?</p>
            <h2 className="mt-3 text-3xl font-black" data-id="element-263">Somos Avanza Pro.</h2>
            <p className="mt-1 text-sm font-bold text-emerald-700" data-id="element-264">
              La Facultad del Nuevo Mundo.
            </p>
            <p className="mt-4 text-sm leading-6 text-slate-600" data-id="element-265">
              Creamos sistemas digitales para personas que quieren pensar mejor,
              decidir mejor y construir una vida más soberana.
            </p>
            <p className="mt-3 text-sm leading-6 text-slate-600" data-id="element-266">
              Pensamientos Fuera de Serie nace para quienes sienten que el
              modelo tradicional no les enseñó lo más importante: cómo
              conocerse, cómo construir metas reales, sostener hábitos, crear
              autoridad y vivir con libertad.
            </p>
            <p className="mt-3 text-sm font-semibold text-emerald-900" data-id="element-267">
              No creemos en la motivación vacía. Creemos en la claridad, la
              ejecución, la identidad y el pensamiento profundo aplicado a la
              vida real.
            </p>
            <p className="mt-5 text-sm" data-id="element-268">
              Encontrá todos nuestros recursos en{' '}
              <a href="https://pensamientosfueradeserie.avanzapro.store" className="font-bold text-emerald-700 underline underline-offset-2" data-id="element-269">
                www.avanzapro.store
              </a>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-shell bg-[#edf5f0]" data-id="element-270">
        <Reveal data-id="element-271">
          <SectionHeading eyebrow="PREGUNTAS FRECUENTES" title="Todo lo que necesitás saber antes de comprar." data-id="element-272" />
        </Reveal>
        <FAQAccordion items={faqs} data-id="element-273" />
      </section>

      <section className="section-shell bg-emerald-700 text-center text-white" data-id="element-274">
        <Reveal data-id="element-275">
          <div className="mx-auto max-w-3xl" data-id="element-276">
            <p className="text-xs font-black tracking-[0.15em] text-amber-300" data-id="element-277">
              10 TOMOS EN PDF + KIT DE EJECUCIÓN
            </p>
            <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl" data-id="element-278">
              ¿Estás listo para dejar de postergar tu próxima versión?
            </h2>
            <p className="mt-4 text-sm leading-6 text-emerald-50/85" data-id="element-279">
              La próxima vez que tu mente te diga “no sé por dónde empezar”, “no
              tengo tiempo” o “no estoy listo”, vas a tener una respuesta
              distinta: un sistema, un mapa y una decisión.
            </p>
            <p className="mt-7 text-4xl font-black text-amber-300" data-id="element-280">
              $990 <span className="text-base text-white" data-id="element-281">pesos uruguayos</span>
            </p>
            <p className="mt-2 text-sm font-semibold text-emerald-100" data-id="element-282">
              Antes: <span className="line-through" data-id="element-283">$1.990</span> · Oferta de
              lanzamiento por tiempo limitado
            </p>
            <PurchaseButton className="mt-7" data-id="element-284">
              ASEGURAR MI BIBLIOTECA COMPLETA
            </PurchaseButton>
            <PaymentNote inverted data-id="element-285" />
          </div>
        </Reveal>
      </section>

      <section className="bg-[#062f27] px-5 py-12 text-center text-emerald-50 sm:px-8" data-id="element-286">
        <blockquote className="mx-auto max-w-3xl text-lg font-bold leading-7" data-id="element-287">
          “No sos tus pensamientos de hoy. Sos la decisión que tomás ahora
          mismo.”
        </blockquote>
        <p className="mt-5 text-sm font-black tracking-[0.14em] text-amber-300" data-id="element-288">
          ✦ AVANZA PRO ✦
        </p>
        <p className="mt-1 text-xs text-emerald-100/65" data-id="element-289">
          La Facultad del Nuevo Mundo
        </p>
      </section>

      <footer className="bg-[#031f1a] px-5 py-8 text-center text-xs leading-6 text-emerald-100/65" data-id="element-290">
        <a href="https://pensamientosfueradeserie.avanzapro.store" className="mt-2 inline-block text-emerald-100/80 underline underline-offset-2" data-id="element-291">
          www.avanzapro.store
        </a>
        <nav aria-label="Redes sociales de Avanza Pro" className="mt-5 flex justify-center gap-3" data-id="element-292">
          <a href="https://www.instagram.com/avanzapro_oficial/" target="_blank" rel="noreferrer" aria-label="Seguir a Avanza Pro en Instagram" className="grid h-10 w-10 place-items-center rounded-full border border-emerald-100/20 text-emerald-50 transition hover:border-amber-300 hover:bg-emerald-900 hover:text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300" data-id="element-293">
            <InstagramIcon size={18} aria-hidden="true" data-id="element-294" />
          </a>
          <a href="https://www.facebook.com/profile.php?id=61590326735050" target="_blank" rel="noreferrer" aria-label="Seguir a Avanza Pro en Facebook" className="grid h-10 w-10 place-items-center rounded-full border border-emerald-100/20 text-sm font-black text-emerald-50 transition hover:border-amber-300 hover:bg-emerald-900 hover:text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300" data-id="element-295">
            f
          </a>
          <a href="https://www.youtube.com/@avanzapro_ok" target="_blank" rel="noreferrer" aria-label="Suscribirse a Avanza Pro en YouTube" className="grid h-10 w-10 place-items-center rounded-full border border-emerald-100/20 text-emerald-50 transition hover:border-amber-300 hover:bg-emerald-900 hover:text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300" data-id="element-296">
            <YoutubeIcon size={19} aria-hidden="true" data-id="element-297" />
          </a>
        </nav>
        <p className="mt-5" data-id="element-298">
          © 2026 Avanza Pro — Todos los derechos reservados.
        </p>
        <p className="mt-2" data-id="element-299">
          Pago seguro procesado mediante Mercado Pago · Transacciones protegidas
        </p>
        <p data-id="element-300">
          Producto 100% digital en PDF · Sin envíos físicos · Sin impresión
          necesaria
        </p>
        <a href="https://drive.google.com/file/d/1get8d3Gz1tckuwidvBmreEazNpLa0zav/view?usp=sharing" target="_blank" rel="noreferrer" className="mx-auto mt-5 inline-flex min-h-10 items-center justify-center rounded-full border border-emerald-100/25 px-4 text-xs font-bold text-emerald-50 transition hover:border-amber-300 hover:text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300" data-id="element-301">
          Términos y condiciones
        </a>
      </footer>
    </main>;
}